import { Button } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { FiatIcon, FilterIcon } from 'BreetAssets';
import { ContentWrapper, type DateRangeFilterValue, DateRangePicker, Loader, PageHeader, Seo } from 'BreetComponents';
import { capitalizeFirstLetters, type CURRENCYCODETYPE, handleApiError } from 'BreetHelpers';
import { selectFiatMeta, useAppSelector, useGetVIPStatsGraphQuery, useGetVIPStatsQuery } from 'BreetRedux';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
	ANALYTICS_PERIODS,
	ANALYTICS_TABS,
	type AnalyticsPeriod,
	type AnalyticsTab,
	getActivePeriod,
	getPeriodDateRange,
	type IAnalytics,
} from './Analytics.helpers';
import { AnalyticsStats } from './Analytics.Stats';

const AnalyticsChart = lazy(() => import('./Analytics.Chart').then((module) => ({ default: module.AnalyticsChart })));

const formatRangeDay = (date: Date): string => format(date, 'MMM d, yyyy');
const formatRangeDateTime = (date: Date): string => format(date, 'MMM d, yyyy · h a');

const Analytics = ({ hideStats = false, page = false, viewOnly }: IAnalytics) => {
	const fiatmetaProps = useAppSelector(selectFiatMeta);

	const [activePeriod, setActivePeriod] = useState<AnalyticsPeriod>('7days');
	const [activeCurrency, setActiveCurrency] = useState<CURRENCYCODETYPE>(fiatmetaProps.currency);
	const [activeTab, setActiveTab] = useState<AnalyticsTab>('Transactions');

	const [showFilter, setShowFilter] = useState(false);
	const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
	const filterBtnRef = useRef<HTMLButtonElement>(null);
	const filterDropRef = useRef<HTMLDivElement>(null);

	const [vipStatsArg, setVipStatsArg] = useState<Record<string, string> | undefined>(() => getPeriodDateRange('7days'));
	const [appliedRange, setAppliedRange] = useState<{ start: Date; end: Date; hasTime: boolean } | null>(null);

	const { isLoading, data: vipStatsData, isFetching, refetch: refetchVIPStats } = useGetVIPStatsQuery(vipStatsArg);
	const {
		isLoading: isVipStatsGraphLoading,
		data: vipStatsGraphData,
		isFetching: isFetchingVipStatsGraph,
		refetch: refetchVIPGraphStats,
	} = useGetVIPStatsGraphQuery(
		{ range: activePeriod, currency: activeCurrency.toLowerCase() },
		{ refetchOnMountOrArgChange: true }
	);

	const refetchAll = useCallback(() => {
		refetchVIPStats().catch(handleApiError);
		refetchVIPGraphStats().catch(handleApiError);
	}, [refetchVIPStats, refetchVIPGraphStats]);

	useEffect(() => {
		if (!showFilter) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			const insideBtn = filterBtnRef.current?.contains(target);
			const insideDrop = filterDropRef.current?.contains(target);
			if (!insideBtn && !insideDrop) setShowFilter(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [showFilter]);

	useLayoutEffect(() => {
		if (!showFilter || !filterDropRef.current) return;
		const margin = 8;
		const panelHeight = filterDropRef.current.offsetHeight;
		const maxTop = Math.max(margin, window.innerHeight - panelHeight - margin);
		setDropdownPos((pos) => (pos.top <= maxTop ? pos : { ...pos, top: maxTop }));
	}, [showFilter]);

	const handleFilterToggle = useCallback(() => {
		setShowFilter((prev) => {
			if (!prev && filterBtnRef.current) {
				const rect = filterBtnRef.current.getBoundingClientRect();
				setDropdownPos({
					top: rect.bottom + 8,
					right: window.innerWidth - rect.right,
				});
			}
			return !prev;
		});
	}, []);

	const handleFilterApply = useCallback(({ startDate, endDate, utcStartDate, utcEndDate, hasTime }: DateRangeFilterValue) => {
		setVipStatsArg({ startDate: utcStartDate, endDate: utcEndDate });
		setActivePeriod(getActivePeriod(utcStartDate, utcEndDate));
		setAppliedRange({ start: startDate, end: endDate, hasTime });
		setShowFilter(false);
	}, []);

	const handleFilterClear = useCallback(() => {
		const { startDate, endDate } = getPeriodDateRange(activePeriod);
		setVipStatsArg({ startDate, endDate });
		setAppliedRange(null);
		setShowFilter(false);
	}, [activePeriod]);

	const handleFilterClose = useCallback(() => setShowFilter(false), []);

	const handlePeriodChange = useCallback((period: AnalyticsPeriod) => {
		setActivePeriod(period);
		const { startDate, endDate } = getPeriodDateRange(period);
		setVipStatsArg({ startDate, endDate });
		setAppliedRange(null);
	}, []);

	const handlePeriodClick = useCallback(
		(period: AnalyticsPeriod) => () => {
			handlePeriodChange(period);
		},
		[handlePeriodChange]
	);

	const handleCurrencyClick = useCallback(
		(code: CURRENCYCODETYPE) => () => {
			setActiveCurrency(code);
		},
		[]
	);

	const handleTabClick = useCallback(
		(tab: AnalyticsTab) => () => {
			setActiveTab(tab);
		},
		[]
	);

	const dataLoading = isLoading || isFetching || isVipStatsGraphLoading || isFetchingVipStatsGraph;

	const isWithdrawal = activeTab === 'Withdrawals';
	const userCurrency = fiatmetaProps.currency;

	const getThirdWithdrawalCurrency = (): CURRENCYCODETYPE => {
		if (userCurrency !== 'NGN') return 'NGN';
		return 'GHS';
	};

	const updatedAnalyticsCurrencies: CURRENCYCODETYPE[] = isWithdrawal
		? [userCurrency, 'USD', ...(userCurrency !== 'USD' ? [getThirdWithdrawalCurrency()] : [])]
		: [userCurrency, ...(userCurrency !== 'USD' ? ['USD' as CURRENCYCODETYPE] : [])];

	const handleStatsTabClick = useCallback(
		(tab: AnalyticsTab) => () => {
			setActiveTab(tab);

			if (tab !== 'Withdrawals') {
				setActiveCurrency(userCurrency);
			}
		},
		[setActiveTab, userCurrency]
	);

	return (
		<>
			{page && <Seo page='analytics' />}

			<div className={classNameMerger('appanalytics', page && 'bodycontent')}>
				<ContentWrapper
					hideBreadcrumb
					className='bg-primary-500 border-dark-navy'
				>
					<div className='appanalytics_header'>
						<PageHeader
							pageTitle={viewOnly ? `${capitalizeFirstLetters(viewOnly)} Analytics` : 'Analytics'}
							isLoading={isFetching}
							onRefresh={refetchAll}
						/>

						<div className='appanalytics_filteractions'>
							{appliedRange && (
								<div className='appanalytics_rangechip'>
									<span className='appanalytics_rangechip_text'>
										{(appliedRange.hasTime ? formatRangeDateTime : formatRangeDay)(appliedRange.start)} →{' '}
										{(appliedRange.hasTime ? formatRangeDateTime : formatRangeDay)(appliedRange.end)}
									</span>
									<button
										type='button'
										className='appanalytics_rangechip_clear'
										aria-label='Clear date range'
										onClick={handleFilterClear}
									>
										✕
									</button>
								</div>
							)}

							<Button
								ref={filterBtnRef}
								type='button'
								variant='native'
								className={classNameMerger('appanalytics_filterbtn border-dark-navy color-neutral-50', showFilter && 'active')}
								onClick={handleFilterToggle}
								trailingIcon={<FilterIcon className='svg-dark' />}
							>
								Filter
							</Button>
						</div>
					</div>

					{showFilter &&
						createPortal(
							<div
								ref={filterDropRef}
								className='appanalytics_filterdropdown'
								style={{ top: dropdownPos.top, right: dropdownPos.right }}
								data-modal-ignore='true'
							>
								<DateRangePicker
									onApply={handleFilterApply}
									onClear={handleFilterClear}
									onClose={handleFilterClose}
									loading={dataLoading}
									initialStartDate={appliedRange?.start ?? null}
									initialEndDate={appliedRange?.end ?? null}
								/>
							</div>,
							document.body
						)}

					<div className='appanalytics_controls'>
						<div className='appanalytics_periodtabs'>
							{ANALYTICS_PERIODS.map(({ label, value }) => (
								<Button
									key={value}
									type='button'
									className={classNameMerger(
										'appanalytics_periodtab',
										activePeriod === value && !appliedRange
											? 'active bg-primary-400 border-primary-400 color-neutral-50'
											: 'color-primary-300'
									)}
									onClick={handlePeriodClick(value)}
									disabled={dataLoading}
								>
									{label}
								</Button>
							))}
						</div>

						<div className='appanalytics_currencies'>
							{updatedAnalyticsCurrencies.map((code) => (
								<button
									key={code}
									type='button'
									className={classNameMerger('appanalytics_currency', activeCurrency === code && 'active bg-primary-400')}
									onClick={handleCurrencyClick(code)}
								>
									<FiatIcon
										currencyCode={code}
										variant='round'
										width={18}
										height={18}
									/>
									<span className='color-neutral-50 text-box-trim'>{code}</span>
								</button>
							))}
						</div>
					</div>

					{hideStats && !viewOnly && (
						<div className='appanalytics_serieslegend'>
							{ANALYTICS_TABS.map((tab) => (
								<button
									key={tab}
									type='button'
									className={classNameMerger(
										'appanalytics_serieschip',
										activeTab === tab ? 'active bg-primary-400 color-neutral-50' : 'color-primary-300'
									)}
									onClick={handleTabClick(tab)}
								>
									<span className='appanalytics_serieschip_dot' />
									{tab}
								</button>
							))}
						</div>
					)}

					<Suspense fallback={<Loader bgClassName='bg-primary-500' />}>
						<AnalyticsChart
							dataLoading={dataLoading}
							vipStatsGraphData={vipStatsGraphData}
							activeTab={activeTab}
							activePeriod={activePeriod}
							activeCurrency={activeCurrency}
						/>
					</Suspense>

					{!hideStats && (
						<AnalyticsStats
							vipStatsData={vipStatsData}
							activeTab={activeTab}
							handleStatsTabClick={handleStatsTabClick}
							isLoading={isFetching}
						/>
					)}
				</ContentWrapper>
			</div>
		</>
	);
};

export default Analytics;
